import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import {
  MdAutorenew, MdSchedule, MdTune, MdMailOutline, MdVisibility, MdCode, MdContentCopy,
  MdContentPaste, MdRestore, MdSave, MdSmartToy, MdDesktopWindows, MdSmartphone, MdInsights, MdTextFields, MdStar, MdDeleteOutline,
} from 'react-icons/md';
import EmailContentEditor from '../pageInside/emailContentKit/EmailContentEditor';
import { elakay_API } from '../../config';
import { isAuthenticated } from '../auth';
import { getCurrentElakayRole } from '../pageInside/Rolle';
import labels from './weeklyIncomeLabels.json';
import { getElakayLanguageCode } from '../pageInside/dataElakay';
import './WeeklyIncomeSettings.css';

// GET without body, PATCH with a body (historical calls), or an explicit
// method (POST preview, PUT / DELETE templates).
export async function weeklyIncomeRequest(path, token, body, method) {
  const verb = method || (body ? 'PATCH' : 'GET');
  const response = await fetch(`${elakay_API}/weekly-income/${path}`, { method: verb, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, ...(body ? { body: JSON.stringify(body) } : {}) });
  const data = await response.json();
  // details: the server's validation report (errors / warnings / tokens).
  if (!response.ok) throw Object.assign(new Error(data.error || `HTTP ${response.status}`), { status: response.status, details: data.details });
  return data;
}
// Missing keys of a language fall back to English.
const text = () => ({ ...labels.en, ...(labels[getElakayLanguageCode()] || {}) });
const ROLES = ['Seller', 'Provider', 'Associate', 'Investment', 'Administrator', 'Family'];
const LANGUAGES = [['en', 'English'], ['fr', 'Français'], ['es', 'Español'], ['ht', 'Kreyòl'], ['pt', 'Português']];
const TRACKING_TILES = ['eligibleUsers', 'reportsPrepared', 'accepted', 'delivered', 'pending', 'failed', 'unknown', 'skipped'];
const timezones = (current) => {
  let list = [];
  try { list = Intl.supportedValuesOf?.('timeZone') || []; } catch { list = []; }
  return list.includes(current) ? list : [current, ...list];
};
const previewLanguage = () => (LANGUAGES.some(([code]) => code === getElakayLanguageCode()) ? getElakayLanguageCode() : 'en');

// role="switch" instead of a native checkbox: mdbreact's global mdb.css
// hides/blocks native checkboxes across front_elakay.
function Switch({ checked, onChange, label, disabled = false }) {
  return <button type="button" role="switch" aria-checked={Boolean(checked)} aria-label={label} disabled={disabled}
    className={`wie-switch${checked ? ' is-on' : ''}`} onClick={() => onChange(!checked)}><span className="wie-switch__knob" /></button>;
}

function ToggleRow({ label, hint, checked, onChange, disabled }) {
  return <div className="wie-toggle-row">
    <div><div className="wie-toggle-row__label">{label}</div>{hint && <div className="wie-hint">{hint}</div>}</div>
    <Switch label={label} checked={checked} onChange={onChange} disabled={disabled} />
  </div>;
}

const copyText = async (value) => {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(value);
  const area = document.createElement('textarea');
  area.value = value; document.body.appendChild(area); area.select();
  document.execCommand('copy'); area.remove();
  return undefined;
};

// Sandboxed: the email HTML is shown, never executed with this app's origin.
function EmailFrame({ html, device, title }) {
  return <div className={`wie-frame wie-frame--${device}`}>
    <iframe title={title} sandbox="" srcDoc={html || ''} />
  </div>;
}

function DeviceToggle({ t, device, setDevice }) {
  return <div className="wie-segment" role="group" aria-label={`${t.desktop} / ${t.mobile}`}>
    <button type="button" aria-pressed={device === 'desktop'} className={device === 'desktop' ? 'is-active' : ''} onClick={() => setDevice('desktop')}><MdDesktopWindows aria-hidden="true" /> {t.desktop}</button>
    <button type="button" aria-pressed={device === 'mobile'} className={device === 'mobile' ? 'is-active' : ''} onClick={() => setDevice('mobile')}><MdSmartphone aria-hidden="true" /> {t.mobile}</button>
  </div>;
}

function PreviewModal({ token, t, initialRole, onClose, onEdit, onEditTexts }) {
  const [role, setRole] = useState(initialRole);
  const [language, setLanguage] = useState(previewLanguage);
  const [scenario, setScenario] = useState('income');
  const [device, setDevice] = useState('desktop');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    setError('');
    weeklyIncomeRequest('preview', token, { role, language, scenario }, 'POST')
      .then((value) => { if (active) setResult(value); })
      .catch((e) => { if (active) setError(e.message); });
    return () => { active = false; };
  }, [token, role, language, scenario]);
  return <Modal show onHide={onClose} size="xl" centered dialogClassName="wie-modal" aria-labelledby="wie-preview-title">
    <Modal.Header closeButton closeLabel={t.close}>
      <Modal.Title id="wie-preview-title"><MdVisibility aria-hidden="true" /> {t.preview}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <div className="wie-role-tabs" role="group" aria-label={t.templates}>
        {ROLES.map((name) => <button key={name} type="button" aria-pressed={role === name} className={role === name ? 'is-active' : ''} onClick={() => setRole(name)}>{t.roleNames[name]}</button>)}
      </div>
      <div className="wie-toolbar">
        <label className="wie-field wie-field--inline">{t.language}
          <select value={language} onChange={(e) => setLanguage(e.target.value)}>{LANGUAGES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}</select>
        </label>
        <label className="wie-field wie-field--inline">{t.sampleData}
          <select value={scenario} onChange={(e) => setScenario(e.target.value)}><option value="income">{t.withIncome}</option><option value="zero">{t.zeroWeek}</option></select>
        </label>
        <DeviceToggle t={t} device={device} setDevice={setDevice} />
        <button type="button" className="wie-btn wie-btn--ghost" onClick={() => onEditTexts(role, language)}><MdTextFields aria-hidden="true" /> {t.editTexts}</button>
        <button type="button" className="wie-btn wie-btn--ghost" onClick={() => onEdit(role)}><MdCode aria-hidden="true" /> {t.editHtml}</button>
      </div>
      {error && <div className="wie-alert" role="alert">{error}</div>}
      {result && <>
        <div className="wie-subject"><span>{t.subject}</span><strong>{result.subject}</strong><span className={`wie-badge${result.customized ? ' is-custom' : ''}`}>{result.customized ? t.custom : t.defaultTemplate}</span></div>
        <EmailFrame html={result.html} device={device} title={`${t.preview} — ${t.roleNames[role]}`} />
        <p className="wie-hint">{t.sampleNote}</p>
      </>}
    </Modal.Body>
  </Modal>;
}

function HtmlConsoleModal({ token, t, role, onClose, onSaved }) {
  const [template, setTemplate] = useState(null);
  const [html, setHtml] = useState('');
  const [language, setLanguage] = useState(previewLanguage);
  const [device, setDevice] = useState('desktop');
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const area = useRef(null);
  useEffect(() => {
    let active = true;
    weeklyIncomeRequest(`templates/${role}`, token)
      .then((value) => { if (active) { setTemplate(value); setHtml(value.html); } })
      .catch((e) => { if (active) setError(e.message); });
    return () => { active = false; };
  }, [token, role]);
  // Live preview of the console content (debounced); a validation message
  // (missing {{unsubscribeUrl}}, unknown token, script) is shown, not thrown.
  useEffect(() => {
    if (!template) return undefined;
    let active = true;
    const timer = setTimeout(() => {
      weeklyIncomeRequest('preview', token, { role, language, scenario: 'income', html }, 'POST')
        .then((value) => { if (active) { setPreview(value); setPreviewError(''); } })
        .catch((e) => { if (active) setPreviewError(e.message); });
    }, 450);
    return () => { active = false; clearTimeout(timer); };
  }, [template, token, role, language, html]);
  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(''), 3500);
    return () => clearTimeout(timer);
  }, [notice]);
  const dirty = Boolean(template) && html !== template.html;
  const close = () => { if (!dirty || window.confirm(t.confirmDiscard)) onClose(); };
  const insertToken = (name) => {
    const el = area.current;
    const tokenText = `{{${name}}}`;
    const start = el ? el.selectionStart : html.length;
    const end = el ? el.selectionEnd : html.length;
    setHtml(`${html.slice(0, start)}${tokenText}${html.slice(end)}`);
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(start + tokenText.length, start + tokenText.length); } });
  };
  const paste = async () => {
    try {
      const value = await navigator.clipboard.readText();
      if (value) setHtml(value);
    } catch {
      setNotice(t.pasteBlocked);
      area.current?.focus();
    }
  };
  const save = async () => {
    setSaving(true); setError('');
    try {
      const value = html === template.defaultHtml && template.customized
        ? await weeklyIncomeRequest(`templates/${role}`, token, null, 'DELETE')
        : await weeklyIncomeRequest(`templates/${role}`, token, { html }, 'PUT');
      setTemplate(value); setHtml(value.html); setNotice(t.templateSaved); onSaved();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };
  return <Modal show onHide={close} size="xl" centered dialogClassName="wie-modal wie-modal--console" aria-labelledby="wie-console-title">
    <Modal.Header closeButton closeLabel={t.close}>
      <Modal.Title id="wie-console-title"><MdCode aria-hidden="true" /> {t.htmlConsole} — {t.roleNames[role]}
        {template && <span className={`wie-badge${template.customized ? ' is-custom' : ''}`}>{template.customized ? t.custom : t.defaultTemplate}</span>}
        {dirty && <span className="wie-badge is-warning">{t.unsaved}</span>}
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p className="wie-ai-help"><MdSmartToy aria-hidden="true" /> {t.aiHelp}</p>
      <div className="wie-toolbar">
        <button type="button" className="wie-btn wie-btn--ghost" disabled={!template} onClick={() => copyText(html).then(() => setNotice(t.copied))}><MdContentCopy aria-hidden="true" /> {t.copy}</button>
        <button type="button" className="wie-btn wie-btn--ghost" disabled={!template} onClick={() => copyText(`${t.aiPrompt.replace('{role}', t.roleNames[role])}${html}`).then(() => setNotice(t.aiCopied))}><MdSmartToy aria-hidden="true" /> {t.copyAi}</button>
        <button type="button" className="wie-btn wie-btn--ghost" disabled={!template} onClick={paste}><MdContentPaste aria-hidden="true" /> {t.paste}</button>
        <button type="button" className="wie-btn wie-btn--ghost" disabled={!template || html === template.defaultHtml} onClick={() => { if (window.confirm(t.confirmRestore)) setHtml(template.defaultHtml); }}><MdRestore aria-hidden="true" /> {t.restoreDefault}</button>
        <span className="wie-toolbar__spacer" />
        <button type="button" className="wie-btn wie-btn--primary" disabled={!template || saving || !dirty} onClick={save}><MdSave aria-hidden="true" /> {saving ? t.saving : t.saveTemplate}</button>
      </div>
      {notice && <div className="wie-notice" role="status">{notice}</div>}
      {error && <div className="wie-alert" role="alert">{error}</div>}
      <div className="wie-console">
        <div className="wie-console__editor">
          <textarea ref={area} aria-label={t.htmlConsole} spellCheck={false} value={html} onChange={(e) => setHtml(e.target.value)} disabled={!template} />
          {template && <div className="wie-tokens">
            <div className="wie-tokens__title">{t.tokens}</div>
            {template.tokens.map((name) => <button key={name} type="button" className="wie-token" title={t.tokenHelp?.[name] || name} onClick={() => insertToken(name)}>{`{{${name}}}`}</button>)}
          </div>}
        </div>
        <div className="wie-console__preview">
          <div className="wie-toolbar wie-toolbar--tight">
            <strong>{t.livePreview}</strong>
            <span className="wie-toolbar__spacer" />
            <select aria-label={t.language} value={language} onChange={(e) => setLanguage(e.target.value)}>{LANGUAGES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}</select>
            <DeviceToggle t={t} device={device} setDevice={setDevice} />
          </div>
          {previewError && <div className="wie-alert" role="alert">{previewError}</div>}
          {preview && <EmailFrame html={preview.html} device={device} title={`${t.livePreview} — ${t.roleNames[role]}`} />}
        </div>
      </div>
    </Modal.Body>
  </Modal>;
}

// Friendly labels of the built-in texts in the content form (same labels as
// the former "Edit texts" window); keys Family adds keep their own name.
const builtInLabel = (t, defaults) => (parts) => {
  const [group, sub] = parts;
  if (parts.length === 1 && t.textFields?.[group]) return t.textFields[group];
  if (parts.length === 1 && t.textGroups?.[group]) return t.textGroups[group];
  if (parts.length === 2 && group === 'tips') return `${t.textGroups.tips} — ${defaults?.[sub] || sub}`;
  if (parts.length === 2 && (group === 'responsibility' || group === 'roles')) return `${t.textGroups[group]} — ${t.roleNames[sub] || sub}`;
  return undefined;
};

const languageLabelOf = (code) => LANGUAGES.find(([c]) => c === code)?.[1] || code;
const roleScenarioControls = (t, role, setRole, scenario, setScenario) => <>
  <label className="wie-field wie-field--inline">{t.role}
    <select value={role} onChange={(e) => setRole(e.target.value)}>{ROLES.map((name) => <option key={name} value={name}>{t.roleNames[name]}</option>)}</select>
  </label>
  <label className="wie-field wie-field--inline">{t.sampleData}
    <select value={scenario} onChange={(e) => setScenario(e.target.value)}><option value="income">{t.withIncome}</option><option value="zero">{t.zeroWeek}</option></select>
  </label>
</>;

// Shared props of the content editor: one JSON document per language,
// built-in texts locked against removal, {{key}} tokens.
function useContentEditorProps(token, t, content, onSaved) {
  const languages = useMemo(() => {
    if (!content) return [];
    const enabled = content.settings?.enabledLanguages || content.languages;
    return content.languages.filter((code) => code === content.sourceLanguage || enabled.includes(code));
  }, [content]);
  const onSaveDocument = useCallback(async (language, document) => {
    const value = await weeklyIncomeRequest(`content/${language}`, token, { content: document }, 'PUT');
    onSaved?.(value);
    return value;
  }, [token, onSaved]);
  const defaultsFlat = content?.defaults?.[previewLanguage()] || content?.defaults?.en;
  return content && {
    languages, sourceLanguage: content.sourceLanguage, documents: content.documents, onSaveDocument, defaults: content.defaults,
    lockedPaths: content.builtInKeys, settings: content.settings, tokenSyntax: 'plain', emailType: 'weekly income report', languageLabel: languageLabelOf,
    labelFor: builtInLabel(t, defaultsFlat),
    // Weekly Income keys are always {{tokens}} (server rule): letters, digits, _.
    keyPattern: /^[A-Za-z_][A-Za-z0-9_]*$/,
  };
}

// The weekly income content (texts of every language, shared by every
// design) — Form / JSON / Preview / Translations / Validation.
function ContentModal({ token, t, initialLanguage, onClose, onSaved }) {
  const [content, setContent] = useState(null);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [role, setRole] = useState('Seller');
  const [scenario, setScenario] = useState('income');
  useEffect(() => {
    let active = true;
    weeklyIncomeRequest('content', token).then((value) => { if (active) setContent(value); }).catch((e) => { if (active) setError(e.message); });
    return () => { active = false; };
  }, [token]);
  const editorProps = useContentEditorProps(token, t, content, onSaved);
  const renderPreview = useCallback(({ language, document }) => weeklyIncomeRequest('preview', token, { role, scenario, language, content: document }, 'POST'), [token, role, scenario]);
  const close = () => { if (!dirty || window.confirm(t.confirmDiscard)) onClose(); };
  return <Modal show onHide={close} size="xl" centered dialogClassName="wie-modal wie-modal--console" aria-labelledby="wie-content-title">
    <Modal.Header closeButton closeLabel={t.close}>
      <Modal.Title id="wie-content-title"><MdTextFields aria-hidden="true" /> {t.contentTitle}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p className="wie-hint">{t.contentHint}</p>
      {error && <div className="wie-alert" role="alert">{error}</div>}
      {!editorProps && !error && <p role="status">{t.loading}</p>}
      {editorProps && <EmailContentEditor {...editorProps} registry={content.registry} initialLanguage={initialLanguage} onDirtyChange={setDirty}
        renderPreview={renderPreview} previewKey={`${role}|${scenario}`} previewControls={roleScenarioControls(t, role, setRole, scenario, setScenario)} />}
    </Modal.Body>
  </Modal>;
}

// One library design: HTML console + the shared content, live preview.
function TemplateEditorModal({ token, t, templateId, onClose, onSaved }) {
  const [template, setTemplate] = useState(null);
  const [content, setContent] = useState(null);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [role, setRole] = useState('Seller');
  const [scenario, setScenario] = useState('income');
  useEffect(() => {
    let active = true;
    Promise.all([weeklyIncomeRequest(`library/${templateId}`, token), weeklyIncomeRequest('content', token)])
      .then(([tpl, value]) => { if (active) { setTemplate(tpl); setContent(value); } })
      .catch((e) => { if (active) setError(e.message); });
    return () => { active = false; };
  }, [token, templateId]);
  const editorProps = useContentEditorProps(token, t, content, onSaved);
  const onSaveHtml = useCallback(async (html) => {
    const value = await weeklyIncomeRequest(`library/${templateId}`, token, { html }, 'PUT');
    setTemplate(value); onSaved?.();
    return value;
  }, [token, templateId, onSaved]);
  const renderPreview = useCallback(({ language, document, html }) => weeklyIncomeRequest('preview', token, { role, scenario, language, content: document, templateId, html }, 'POST'), [token, role, scenario, templateId]);
  const close = () => { if (!dirty || window.confirm(t.confirmDiscard)) onClose(); };
  return <Modal show onHide={close} size="xl" centered dialogClassName="wie-modal wie-modal--console" aria-labelledby="wie-editor-title">
    <Modal.Header closeButton closeLabel={t.close}>
      <Modal.Title id="wie-editor-title"><MdCode aria-hidden="true" /> {t.editorTitle} — {template?.name || templateId}
        {template && <span className={`wie-badge${template.builtIn ? '' : ' is-custom'}`}>{template.builtIn ? t.builtInBadge : t.customBadge}</span>}
        {template?.edited && <span className="wie-badge is-warning">{t.editedBadge}</span>}
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p className="wie-ai-help"><MdSmartToy aria-hidden="true" /> {t.editorHint}</p>
      {error && <div className="wie-alert" role="alert">{error}</div>}
      {(!editorProps || !template) && !error && <p role="status">{t.loading}</p>}
      {editorProps && template && <EmailContentEditor {...editorProps} registry={template.registry} html={template.html} originalHtml={template.builtIn ? template.originalHtml : undefined}
        onSaveHtml={onSaveHtml} initialTab="html" initialLanguage={previewLanguage()} onDirtyChange={setDirty}
        renderPreview={renderPreview} previewKey={`${role}|${scenario}`} previewControls={roleScenarioControls(t, role, setRole, scenario, setScenario)} />}
    </Modal.Body>
  </Modal>;
}

function TemplatePreviewModal({ token, t, template, onClose, onEdit }) {
  const [role, setRole] = useState('Seller');
  const [language, setLanguage] = useState(previewLanguage);
  const [scenario, setScenario] = useState('income');
  const [device, setDevice] = useState('desktop');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    setError('');
    weeklyIncomeRequest('preview', token, { role, language, scenario, templateId: template.id }, 'POST')
      .then((value) => { if (active) setResult(value); })
      .catch((e) => { if (active) setError(e.message); });
    return () => { active = false; };
  }, [token, role, language, scenario, template.id]);
  return <Modal show onHide={onClose} size="xl" centered dialogClassName="wie-modal" aria-labelledby="wie-tpl-preview-title">
    <Modal.Header closeButton closeLabel={t.close}>
      <Modal.Title id="wie-tpl-preview-title"><MdVisibility aria-hidden="true" /> {template.name}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <div className="wie-toolbar">
        {roleScenarioControls(t, role, setRole, scenario, setScenario)}
        <label className="wie-field wie-field--inline">{t.language}
          <select value={language} onChange={(e) => setLanguage(e.target.value)}>{LANGUAGES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}</select>
        </label>
        <DeviceToggle t={t} device={device} setDevice={setDevice} />
        <span className="wie-toolbar__spacer" />
        <button type="button" className="wie-btn wie-btn--ghost" onClick={() => onEdit(template.id)}><MdCode aria-hidden="true" /> {t.edit}</button>
      </div>
      {error && <div className="wie-alert" role="alert">{error}</div>}
      {result && <>
        <div className="wie-subject"><span>{t.subject}</span><strong>{result.subject}</strong></div>
        <EmailFrame html={result.html} device={device} title={`${t.preview} — ${template.name}`} />
        <p className="wie-hint">{t.sampleNote}</p>
      </>}
    </Modal.Body>
  </Modal>;
}

function DuplicateModal({ token, t, template, onClose, onCreated }) {
  const [name, setName] = useState(`${template.name} (copy)`);
  const [slug, setSlug] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try { onCreated(await weeklyIncomeRequest(`library/${template.id}/duplicate`, token, { name, slug: slug.trim() || undefined }, 'POST')); } catch (err) { setError(err.message); } finally { setSaving(false); }
  };
  return <Modal show onHide={onClose} centered dialogClassName="wie-modal" aria-labelledby="wie-dup-title">
    <form onSubmit={submit}>
      <Modal.Header closeButton closeLabel={t.close}><Modal.Title id="wie-dup-title"><MdContentCopy aria-hidden="true" /> {t.duplicateTitle} — {template.name}</Modal.Title></Modal.Header>
      <Modal.Body>
        <label className="wie-field">{t.nameLabel}<input required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label className="wie-field">{t.slugLabel}<input value={slug} placeholder={`${template.id}-v2`} pattern="[a-z0-9]+(-[a-z0-9]+)*" maxLength={60} onChange={(e) => setSlug(e.target.value.toLowerCase())} /></label>
        <p className="wie-hint">{t.slugHint}</p>
        {error && <div className="wie-alert" role="alert">{error}</div>}
      </Modal.Body>
      <Modal.Footer><button type="submit" className="wie-btn wie-btn--primary" disabled={saving || !name.trim()}><MdContentCopy aria-hidden="true" /> {saving ? t.saving : t.duplicate}</button></Modal.Footer>
    </form>
  </Modal>;
}

// Library cards: the 4 built-in designs + Family's duplicates.
function TemplateLibrary({ token, t, library, reload, onPreview, onEdit, onDuplicate, setError, setNotice }) {
  const [busy, setBusy] = useState('');
  const act = async (key, fn, message) => {
    setBusy(key); setError('');
    try { await fn(); await reload(); if (message) setNotice(message); } catch (e) { setError(e.message); } finally { setBusy(''); }
  };
  const nameOf = (id) => library.templates.find((tpl) => tpl.id === id)?.name || id;
  const sourceText = { default: t.sourceDefault, wallet: t.sourceWallet, fallback: t.sourceFallback }[library.activeSource] || library.activeSource;
  return <>
    <div className="wie-active-design">
      <span>{t.sentNow}</span><strong>{nameOf(library.activeTemplateId)}</strong><span className="wie-hint">({sourceText})</span>
    </div>
    {library.defaultIsFallback && <p className="wie-hint">{t.noDefaultSaved.replace('{name}', nameOf(library.fallbackTemplateId))}</p>}
    {library.roleOverrides.length > 0 && <p className="wie-alert">{t.legacyActive.replace('{roles}', library.roleOverrides.map((role) => t.roleNames[role]).join(', '))}</p>}
    <div className="wie-library">
      {library.templates.map((tpl) => <article key={tpl.id} className={`wie-design${tpl.isDefault ? ' is-default' : ''}`} aria-label={tpl.name}>
        <div className="wie-design__head">
          <strong>{tpl.name}</strong>
          {tpl.isDefault && <span className="wie-badge is-default">{t.defaultBadge}</span>}
          {tpl.isWalletOverride && <span className="wie-badge is-custom">{t.walletBadge}</span>}
        </div>
        <p className="wie-hint">{tpl.description}</p>
        <div className="wie-design__meta">
          <span className="wie-badge">{tpl.builtIn ? t.builtInBadge : t.customBadge}</span>
          {tpl.edited && <span className="wie-badge is-warning">{t.editedBadge}</span>}
          <code>{tpl.id}</code>
        </div>
        <div className="wie-template__actions">
          <button type="button" className="wie-btn wie-btn--ghost" aria-label={`${t.previewBtn} — ${tpl.name}`} onClick={() => onPreview(tpl)}><MdVisibility aria-hidden="true" /> {t.previewBtn}</button>
          <button type="button" className="wie-btn wie-btn--ghost" aria-label={`${t.edit} — ${tpl.name}`} onClick={() => onEdit(tpl.id)}><MdCode aria-hidden="true" /> {t.edit}</button>
          <button type="button" className="wie-btn wie-btn--ghost" aria-label={`${t.duplicate} — ${tpl.name}`} onClick={() => onDuplicate(tpl)}><MdContentCopy aria-hidden="true" /> {t.duplicate}</button>
          <button type="button" className="wie-btn wie-btn--primary" aria-label={`${t.setDefault} — ${tpl.name}`} disabled={Boolean(busy) || (tpl.isDefault && !library.defaultIsFallback)}
            onClick={() => act(`default:${tpl.id}`, () => weeklyIncomeRequest('default-template', token, { templateId: tpl.id }, 'PUT'), t.defaultSet.replace('{name}', tpl.name))}>
            <MdStar aria-hidden="true" /> {tpl.isDefault && !library.defaultIsFallback ? t.defaultBadge : t.setDefault}
          </button>
          {tpl.builtIn && tpl.edited && <button type="button" className="wie-btn wie-btn--ghost" disabled={Boolean(busy)} onClick={() => { if (window.confirm(t.confirmReset.replace('{name}', tpl.name))) act(`reset:${tpl.id}`, () => weeklyIncomeRequest(`library/${tpl.id}/reset`, token, {}, 'POST')); }}><MdRestore aria-hidden="true" /> {t.resetDesign}</button>}
          {!tpl.builtIn && <button type="button" className="wie-btn wie-btn--ghost" aria-label={`${t.delete} — ${tpl.name}`} disabled={Boolean(busy) || tpl.isDefault || tpl.isWalletOverride} onClick={() => { if (window.confirm(t.confirmDelete.replace('{name}', tpl.name))) act(`delete:${tpl.id}`, () => weeklyIncomeRequest(`library/${tpl.id}`, token, null, 'DELETE')); }}><MdDeleteOutline aria-hidden="true" /> {t.delete}</button>}
        </div>
      </article>)}
    </div>
  </>;
}

// Wallet settings section: optional override of the global default design.
export function WeeklyIncomeWalletTemplate({ infoSource }) {
  const { token } = isAuthenticated();
  const t = text(infoSource);
  const [library, setLibrary] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const family = getCurrentElakayRole(infoSource) === 'Family';
  useEffect(() => {
    if (!family || !token) return undefined;
    let active = true;
    weeklyIncomeRequest('library', token).then((value) => { if (active) setLibrary(value); }).catch((e) => { if (active) setError(e.message); });
    return () => { active = false; };
  }, [family, token]);
  if (!family) return null;
  if (!library) return <p role="status" className="wie-hint">{error || t.loading}</p>;
  const nameOf = (id) => library.templates.find((tpl) => tpl.id === id)?.name || id;
  const change = async (value) => {
    setSaving(true); setError(''); setNotice('');
    try { setLibrary(await weeklyIncomeRequest('wallet-template', token, { templateId: value || null }, 'PUT')); setNotice(t.walletSaved); } catch (e) { setError(e.message); } finally { setSaving(false); }
  };
  return <div className="wie wie-wallet">
    <label className="wie-field">{t.walletTemplate}
      <select value={library.walletTemplateId || ''} disabled={saving} onChange={(e) => change(e.target.value)}>
        <option value="">{t.walletUseGlobal.replace('{name}', nameOf(library.effectiveDefaultTemplateId))}</option>
        {library.templates.map((tpl) => <option key={tpl.id} value={tpl.id}>{t.walletUseSpecific.replace('{name}', tpl.name)}</option>)}
      </select>
    </label>
    <p className="wie-hint">{t.walletHint}</p>
    {notice && <div className="wie-notice" role="status">{notice}</div>}
    {error && <div className="wie-alert" role="alert">{error}</div>}
  </div>;
}

export function WeeklyIncomePreference({ infoSource }) {
  const { token } = isAuthenticated();
  const [preference, setPreference] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const t = text(infoSource);
  useEffect(() => { let active = true; if (token) weeklyIncomeRequest('preference', token).then(data => { if (active) setPreference(data); }).catch(e => { if (active) setError(e.message); }); return () => { active = false; }; }, [token]);
  if (!preference?.eligible) return null;
  const change = async (enabled) => { setSaving(true); setError(''); try { setPreference(await weeklyIncomeRequest('preference', token, { enabled })); } catch (e) { setError(e.message); } finally { setSaving(false); } };
  return <section className="settings-section wie">
    <div className="wie-card"><ToggleRow label={t.preference} checked={!!preference.enabled} disabled={saving} onChange={change} /></div>
    {error && <p className="wie-alert" role="alert">{error}</p>}
  </section>;
}

export default function WeeklyIncomeSettings({ infoSource }) {
  const { token } = isAuthenticated();
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [templates, setTemplates] = useState(null);
  const [previewRole, setPreviewRole] = useState(null);
  const [editorRole, setEditorRole] = useState(null);
  const [contentEditor, setContentEditor] = useState(null);
  const [library, setLibrary] = useState(null);
  const [libraryPreview, setLibraryPreview] = useState(null);
  const [libraryEditor, setLibraryEditor] = useState(null);
  const [duplicating, setDuplicating] = useState(null);
  const t = text(infoSource);
  const family = getCurrentElakayRole(infoSource) === 'Family';
  useEffect(() => {
    if (!family || !token) return undefined;
    let active = true;
    const load = () => weeklyIncomeRequest('settings', token).then(value => { if (active) { setData(value); setSettings(previous => previous || value.settings); setSaved(previous => previous || value.settings); setError(''); } }).catch(e => { if (active) setError(e.message); });
    load(); const timer = setInterval(load, 30000);
    return () => { active = false; clearInterval(timer); };
  }, [family, token]);
  const loadTemplates = useCallback(() => {
    if (!family || !token) return;
    weeklyIncomeRequest('templates', token).then(setTemplates).catch(() => setTemplates({ roles: [] }));
  }, [family, token]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  const loadLibrary = useCallback(() => {
    if (!family || !token) return Promise.resolve();
    return weeklyIncomeRequest('library', token).then(setLibrary).catch((e) => setError(e.message));
  }, [family, token]);
  useEffect(() => { loadLibrary(); }, [loadLibrary]);
  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(''), 3000);
    return () => clearTimeout(timer);
  }, [notice]);
  if (!family) return null;
  if (!settings) return <p role="status">{error || t.loading}</p>;
  const update = (key, value) => setSettings(s => ({ ...s, [key]: value }));
  const persist = async (body) => {
    const value = await weeklyIncomeRequest('settings', token, body);
    setSaved(value.settings);
    setData(await weeklyIncomeRequest('settings', token));
    return value.settings;
  };
  // Auto send is saved at once (it is the automation's on/off switch).
  const toggleAutoSend = async (enabled) => {
    setSwitching(true); setError('');
    try { const value = await persist({ enabled }); setSettings(s => ({ ...s, enabled: value.enabled })); } catch (e) { setError(e.message); } finally { setSwitching(false); }
  };
  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try { setSettings(await persist(settings)); setNotice(t.settingsSaved); } catch (err) { setError(err.message); } finally { setSaving(false); }
  };
  const dirty = JSON.stringify(settings) !== JSON.stringify(saved);
  const autoOn = Boolean(saved?.enabled);
  const formatDate = (value) => (value ? new Date(value).toLocaleString(undefined, { timeZone: settings.timezone, dateStyle: 'medium', timeStyle: 'short' }) : '—');
  const last = data?.lastExecution;
  const numberField = (key, min, max) => <label className="wie-field" key={key}>{t[key]}
    <input required type="number" min={min} max={max} value={settings[key]} onChange={e => update(key, Number(e.target.value))} />
  </label>;
  return <section className="settings-section wie">
    <header className="wie-hero">
      <div className="wie-hero__icon"><MdMailOutline aria-hidden="true" /></div>
      <div className="wie-hero__text">
        <h2>{t.title}</h2>
        <p>{t.subtitle}</p>
      </div>
      <div className={`wie-autosend${autoOn ? ' is-on' : ''}`}>
        <div>
          <div className="wie-autosend__label"><MdAutorenew aria-hidden="true" /> {t.autoSend} <span className={`wie-pill${autoOn ? ' is-on' : ''}`}>{autoOn ? t.active : t.paused}</span></div>
          <div className="wie-hint">{autoOn ? t.autoSendOn : t.autoSendOff}</div>
        </div>
        <Switch label={t.autoSend} checked={autoOn} disabled={switching} onChange={toggleAutoSend} />
      </div>
    </header>
    {error && <div className="wie-alert" role="alert">{error}</div>}
    {notice && <div className="wie-notice" role="status">{notice}</div>}

    <form className="wie-grid" onSubmit={submit}>
      <div className="wie-card">
        <h3><MdSchedule aria-hidden="true" /> {t.schedule}</h3>
        <label className="wie-field">{t.dayOfWeek}
          <select value={settings.dayOfWeek} onChange={e => update('dayOfWeek', e.target.value)}>{Object.entries(t.days).map(([key, name]) => <option key={key} value={key}>{name}</option>)}</select>
        </label>
        <label className="wie-field">{t.sendTime}
          <input required type="time" value={settings.sendTime} onChange={e => update('sendTime', e.target.value)} />
        </label>
        <label className="wie-field">{t.timezone}
          <select value={settings.timezone} onChange={e => update('timezone', e.target.value)}>{timezones(settings.timezone).map(zone => <option key={zone} value={zone}>{zone}</option>)}</select>
        </label>
        <div className="wie-next"><span>{t.nextExecution}</span><strong>{formatDate(data?.nextExecution)}</strong><small>{settings.timezone}</small></div>
      </div>
      <div className="wie-card">
        <h3><MdTune aria-hidden="true" /> {t.delivery}</h3>
        <ToggleRow label={t.retryEnabled} hint={t.retryHint} checked={settings.retryEnabled} onChange={v => update('retryEnabled', v)} />
        <ToggleRow label={t.defaultUserPreference} hint={t.defaultHint} checked={settings.defaultUserPreference} onChange={v => update('defaultUserPreference', v)} />
        <div className="wie-fields-3">
          {numberField('batchSize', 1, 500)}
          {numberField('batchDelayMs', 1000, 3600000)}
          {numberField('maxRetries', 0, 10)}
        </div>
      </div>
      <div className="wie-actions">
        {dirty && <span className="wie-badge is-warning">{t.unsaved}</span>}
        <button type="submit" className="wie-btn wie-btn--primary" disabled={saving || !dirty}><MdSave aria-hidden="true" /> {saving ? t.saving : t.save}</button>
      </div>
    </form>

    <div className="wie-card">
      <div className="wie-card__head">
        <h3><MdMailOutline aria-hidden="true" /> {t.designs}</h3>
        <button type="button" className="wie-btn wie-btn--primary" onClick={() => setContentEditor({ language: previewLanguage() })}><MdTextFields aria-hidden="true" /> {t.editContent}</button>
      </div>
      <p className="wie-hint">{t.designsHint}</p>
      {!library ? <p role="status">{t.loading}</p> : <TemplateLibrary token={token} t={t} library={library} reload={loadLibrary} setError={setError} setNotice={setNotice}
        onPreview={setLibraryPreview} onEdit={setLibraryEditor} onDuplicate={setDuplicating} />}
    </div>

    <details className="wie-card wie-legacy" open={Boolean(library?.roleOverrides?.length)}>
      <summary><h3><MdCode aria-hidden="true" /> {t.legacyTitle}</h3></summary>
      <p className="wie-hint">{t.legacyHint}</p>
      <div className="wie-templates">
        {ROLES.map(role => {
          const info = templates?.roles?.find(r => r.role === role);
          return <div key={role} className="wie-template">
            <div className="wie-template__head">
              <strong>{t.roleNames[role]}</strong>
              <span className={`wie-badge${info?.customized ? ' is-custom' : ''}`}>{info?.customized ? t.custom : t.usesDesign}</span>
            </div>
            <div className="wie-template__actions">
              <button type="button" className="wie-btn wie-btn--ghost" aria-label={`${t.view} — ${t.roleNames[role]}`} onClick={() => setPreviewRole(role)}><MdVisibility aria-hidden="true" /> {t.view}</button>
              <button type="button" className="wie-btn wie-btn--ghost" aria-label={`${t.editHtml} — ${t.roleNames[role]}`} onClick={() => setEditorRole(role)}><MdCode aria-hidden="true" /> {t.editHtml}</button>
            </div>
          </div>;
        })}
      </div>
    </details>

    <div className="wie-card">
      <h3><MdInsights aria-hidden="true" /> {t.tracking}</h3>
      {!last ? <p className="wie-hint">{t.noExecution}</p> : <>
        <div className="wie-meta">
          <div><span>{t.status}</span><strong className={`wie-pill is-${last.status}`}>{last.status}</strong></div>
          <div><span>{t.startedAt}</span><strong>{formatDate(last.startedAt)}</strong></div>
          <div><span>{t.periodStart}</span><strong>{formatDate(last.periodStart)}</strong></div>
          <div><span>{t.periodEnd}</span><strong>{formatDate(last.periodEnd)}</strong></div>
        </div>
        <div className="wie-stats">
          {TRACKING_TILES.map(key => <div key={key} className={`wie-stat wie-stat--${key}${last[key] ? '' : ' is-zero'}`}><span>{t[key]}</span><strong>{last[key] ?? '—'}</strong></div>)}
        </div>
        {last.lastError && <div className="wie-alert"><strong>{t.error}:</strong> {last.lastError}</div>}
      </>}
    </div>

    {previewRole && <PreviewModal token={token} t={t} initialRole={previewRole} onClose={() => setPreviewRole(null)} onEdit={(role) => { setPreviewRole(null); setEditorRole(role); }} onEditTexts={(role, language) => { setPreviewRole(null); setContentEditor({ language }); }} />}
    {contentEditor && <ContentModal token={token} t={t} initialLanguage={contentEditor.language} onClose={() => setContentEditor(null)} onSaved={loadLibrary} />}
    {editorRole && <HtmlConsoleModal token={token} t={t} role={editorRole} onClose={() => setEditorRole(null)} onSaved={() => { loadTemplates(); loadLibrary(); }} />}
    {libraryPreview && <TemplatePreviewModal token={token} t={t} template={libraryPreview} onClose={() => setLibraryPreview(null)} onEdit={(id) => { setLibraryPreview(null); setLibraryEditor(id); }} />}
    {libraryEditor && <TemplateEditorModal token={token} t={t} templateId={libraryEditor} onClose={() => { setLibraryEditor(null); loadLibrary(); }} onSaved={loadLibrary} />}
    {duplicating && <DuplicateModal token={token} t={t} template={duplicating} onClose={() => setDuplicating(null)} onCreated={(created) => { setDuplicating(null); loadLibrary(); setNotice(t.duplicated.replace('{name}', created.name)); setLibraryEditor(created.id); }} />}
  </section>;
}
